import { Component, OnInit, AfterViewInit, ViewChild, TemplateRef, HostListener, Input, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableComponent, TableColumn, PaginationConfig } from '../../../shared/components/primeng';
import { AuthApiService, RolesApiService } from '@angular-workspace/services';
import { FormatService } from '../../../shared/services/format.service';

export interface RoleUpgradeRequestData {
  userName: string;
  employeeId: string;
  jobTitle: string;
  currentRole: string;
  requestedDate: string;
  status: string;
  action?: string;
  originalData?: any;
}

@Component({
  selector: 'app-role-upgradation-request',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
  ],
  templateUrl: './role-upgradation-request.component.html',
  styleUrl: './role-upgradation-request.component.scss',
})
export class RoleUpgradationRequestComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() filters: Record<string, any> = {};
  constructor(
    private authApiService: AuthApiService,
    private rolesApiService: RolesApiService,
    private formatService: FormatService,
    private cdr: ChangeDetectorRef
  ) {}

  @ViewChild('roleUpgradeActionCell', { static: false }) roleUpgradeActionCellTemplate!: TemplateRef<any>;
  @ViewChild('roleUpgradeStatusCell', { static: false }) roleUpgradeStatusCellTemplate!: TemplateRef<any>;

  openDropdownId: string | null = null;

  // Role Upgradation Columns (no sorting)
  roleUpgradeColumns: TableColumn[] = [
    {
      key: 'userName',
      label: 'User Name',
      sortable: false,
      width: '15%'
    },
    {
      key: 'employeeId',
      label: 'Employee ID',
      sortable: false,
      width: '12%'
    },
    {
      key: 'jobTitle',
      label: 'Job Title',
      sortable: false,
      width: '15%'
    },
    {
      key: 'currentRole',
      label: 'Current Role',
      sortable: false,
      width: '18%'
    },
    {
      key: 'requestedDate',
      label: 'Requested Date',
      sortable: false,
      width: '15%'
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      width: '10%'
    },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
      width: '10%',
      align: 'left',
      fixed: 'right'
    }
  ];

  // Role Upgradation Data
  roleUpgradeData: RoleUpgradeRequestData[] = [];
  allRoleUpgradeData: RoleUpgradeRequestData[] = [];

  // Pagination for Role Upgrade
  roleUpgradePagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100]
  };

  loading = false;
  roleUpgradeEmptyMessage = 'No role upgradation requests found';
  private isInitialLoad = true;
  private previousFilters: Record<string, any> = {};
  private loadInProgress = false;

  async ngOnInit(): Promise<void> {
    // Store initial filters
    this.previousFilters = this.filters ? JSON.parse(JSON.stringify(this.filters)) : {};
    
    await this.loadRoleUpgradeRequests();
    
    // Trigger change detection after initial load
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    // Mark initial load as complete after a short delay
    setTimeout(() => {
      this.isInitialLoad = false;
      this.cdr.markForCheck();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Skip filter changes during initial load
    if (this.isInitialLoad) {
      return;
    }

    if (changes['filters'] && !changes['filters'].firstChange) {
      // Compare filters to see if they actually changed
      const currentFiltersStr = JSON.stringify(this.filters || {});
      const previousFiltersStr = JSON.stringify(this.previousFilters);
      
      if (currentFiltersStr !== previousFiltersStr) {
        // Filters actually changed, update and reload
        this.previousFilters = this.filters ? JSON.parse(JSON.stringify(this.filters)) : {};
        this.roleUpgradePagination = { ...this.roleUpgradePagination, currentPage: 1 };
        this.loadRoleUpgradeRequests();
      }
    }
  }

  ngAfterViewInit(): void {
    // Assign templates synchronously to avoid race condition with data loading
    if (this.roleUpgradeActionCellTemplate) {
      const actionColumn = this.roleUpgradeColumns.find(col => col.key === 'action');
      if (actionColumn) {
        actionColumn.cellTemplate = this.roleUpgradeActionCellTemplate;
      }
    }
    if (this.roleUpgradeStatusCellTemplate) {
      const statusColumn = this.roleUpgradeColumns.find(col => col.key === 'status');
      if (statusColumn) {
        statusColumn.cellTemplate = this.roleUpgradeStatusCellTemplate;
      }
    }
  }


  async loadRoleUpgradeRequests(): Promise<void> {
    // Prevent concurrent API calls
    if (this.loadInProgress) {
      return;
    }

    this.loadInProgress = true;
    this.loading = true;
    try {
      const page = this.roleUpgradePagination.currentPage;
      const size = this.roleUpgradePagination.pageSize;
      const dateRange = this.filters?.['dateRange'] || null;

      // Forward the selected date range so the API receives time/endtime/timerel.
      const response = await this.authApiService.providerRoleRequest(page, size, dateRange);

      // Map API response to RoleUpgradeRequestData format
      let mappedData: RoleUpgradeRequestData[] = [];
      let totalItems = 0;

      if (response) {
        // Handle different response structures
        if (response['result'] && Array.isArray(response['result'])) {
          mappedData = response['result'].map((item: any) => this.mapRoleUpgradeItem(item));
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (response['data'] && Array.isArray(response['data'])) {
          mappedData = response['data'].map((item: any) => this.mapRoleUpgradeItem(item));
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || mappedData.length;
        } else if (Array.isArray(response)) {
          mappedData = response.map((item: any) => this.mapRoleUpgradeItem(item));
          totalItems = mappedData.length;
        }
      }

      this.roleUpgradeData = mappedData;
      this.allRoleUpgradeData = [...mappedData];
      this.roleUpgradePagination = {
        ...this.roleUpgradePagination,
        totalItems: totalItems
      };
    } catch (error) {
      console.error('Error loading role upgrade requests:', error);
      this.roleUpgradeData = [];
      this.allRoleUpgradeData = [];
      this.roleUpgradePagination = {
        ...this.roleUpgradePagination,
        totalItems: 0
      };
    } finally {
      this.loading = false;
      this.loadInProgress = false;
      
      // Trigger change detection to ensure view updates
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      
      // Also trigger change detection in next microtask to ensure view updates
      Promise.resolve().then(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      });
    }
  }

  onRoleUpgradePageChange(page: number): void {
    this.roleUpgradePagination = {
      ...this.roleUpgradePagination,
      currentPage: page
    };
    this.loadRoleUpgradeRequests();
  }

  onRoleUpgradePageSizeChange(pageSize: number): void {
    this.roleUpgradePagination = {
      ...this.roleUpgradePagination,
      pageSize: pageSize,
      currentPage: 1
    };
    this.loadRoleUpgradeRequests();
  }

  toggleActionDropdown(event: Event, row: RoleUpgradeRequestData): void {
    event.stopPropagation();
    event.preventDefault();
    const rowId = this.getRowId(row);
    const wasOpen = this.openDropdownId === rowId;
    this.openDropdownId = wasOpen ? null : rowId;
    
    // Trigger change detection to ensure dropdown visibility updates
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    // Position dropdown using fixed positioning to escape scroll container
    if (!wasOpen && this.openDropdownId === rowId) {
      setTimeout(() => {
        const button = (event.target as HTMLElement).closest('.action-btn') as HTMLElement;
        const dropdown = document.querySelector(`[data-dropdown-id="${rowId}"]`) as HTMLElement;
        if (button && dropdown) {
          const rect = button.getBoundingClientRect();
          dropdown.style.position = 'fixed';
          dropdown.style.top = `${rect.bottom + 4}px`;
          dropdown.style.right = `${window.innerWidth - rect.right}px`;
          dropdown.style.left = 'auto';
          dropdown.style.width = 'max-content';
          dropdown.style.minWidth = '120px';
          
          // Trigger change detection to ensure dropdown is visible
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        }
      }, 0);
    }
  }

  closeActionDropdown(): void {
    this.openDropdownId = null;
  }

  isDropdownOpen(row: RoleUpgradeRequestData): boolean {
    const rowId = this.getRowId(row);
    return this.openDropdownId === rowId;
  }

  getRowId(row: RoleUpgradeRequestData): string {
    return row.originalData?.id || JSON.stringify(row);
  }

  /**
   * Check if status is pending
   */
  isStatusPending(status: string): boolean {
    if (!status) return false;
    return status.toLowerCase() === 'pending';
  }

  /**
   * Check if status is granted or rejected (action should be disabled)
   */
  isStatusProcessed(status: string): boolean {
    if (!status) return false;
    const normalizedStatus = status.toLowerCase().trim();
    return normalizedStatus === 'granted' || normalizedStatus === 'approved' || normalizedStatus === 'rejected';
  }

  /**
   * Map API response item to RoleUpgradeRequestData format
   * Extracts highest role from roles array for currentRole
   */
  private mapRoleUpgradeItem(item: any): RoleUpgradeRequestData {
    // Get highest role from roles array
    let currentRole = '';
    if (item.roles && Array.isArray(item.roles) && item.roles.length > 0) {
      const highestRole = this.rolesApiService.getHighestRole(item.roles);
      if (highestRole) {
        // Format role name: convert "org-admin" to "Org Admin", "provider" to "Provider", etc.
        currentRole = highestRole
          .split('-')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
      } else {
        // Fallback: use the first role if getHighestRole returns null
        currentRole = item.roles[0] || '';
      }
    } else {
      // Fallback to existing currentRole field if roles array is not available
      currentRole = item.currentRole || item.current_role || '';
    }

    // Format the requested date
    const rawDate = item.requestedDate || item.requested_date || item.createdAt || item.created_at || '';
    const formattedDate = rawDate ? this.formatService.formatDate(rawDate) : '';

    return {
      userName: item.userName || item.user_name || item.name || '',
      employeeId: item.employeeId || item.employee_id || item.empId || '',
      jobTitle: item.jobTitle || item.job_title || item.title || '',
      currentRole: currentRole,
      requestedDate: formattedDate,
      status: item.status || item.requestStatus || item.request_status || '',
      action: 'View',
      originalData: item
    };
  }

  async handleRoleUpgradeAction(action: string, row: RoleUpgradeRequestData): Promise<void> {
    this.closeActionDropdown();
    console.log(`${action} action for role upgrade request:`, row);
    
    // Get the request ID from the original data
    const requestId = row.originalData?.id;
    if (!requestId) {
      console.error('Request ID not found in row data');
      return;
    }

    try {
      let payload: { status: string };
      
    if (action === 'approve') {
        payload = { status: 'granted' };
    } else if (action === 'reject') {
        payload = { status: 'rejected' };
      } else {
        console.error('Invalid action:', action);
        return;
      }

      // Call the API to approve/reject the provider role request
      await this.authApiService.approveRejectProviderRole(requestId, payload);
      
      // Reload the data to reflect the updated status
      await this.loadRoleUpgradeRequests();
      
      // Ensure change detection is triggered after reload
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      
      console.log(`Successfully ${action}d provider role request:`, requestId);
    } catch (error) {
      console.error(`Error ${action}ing provider role request:`, error);
      // Error handling is done by the NetworkService (toaster messages)
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Close dropdown when clicking outside
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeActionDropdown();
    }
  }
}
